import React, { useState, useEffect } from 'react';
import { 
  Eye, Users, ShoppingCart, Store, Monitor, RefreshCw, 
  Clock, DollarSign, User, Package, Loader2, AlertCircle,
  CheckCircle, TrendingUp, Activity
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { shoppingSessionAPI } from '../services/api';
import { toast } from 'sonner';

const GeneralOverseer = () => {
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Fetch data
  const fetchData = async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    try {
      const [sessionsData, statsData] = await Promise.all([
        shoppingSessionAPI.getActive(),
        shoppingSessionAPI.getStats()
      ]);
      setSessions(sessionsData);
      setStats(statsData);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (showRefreshing) {
        toast.error('Failed to refresh data');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, []);

  // Auto-refresh every 10 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      fetchData();
    }, 10000);

    return () => clearInterval(interval);
  }, [autoRefresh]);

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    return `${diffHours}h ${diffMins % 60}m ago`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading shopping activity...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <Eye className="w-8 h-8 text-purple-600" />
            General Overseer
          </h1>
          <p className="text-gray-500 mt-1">Monitor all customers shopping in real-time</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Auto-refresh</span>
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                autoRefresh ? 'bg-green-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  autoRefresh ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          <Button
            variant="outline"
            onClick={() => fetchData(true)}
            disabled={refreshing}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Online Shoppers</p>
                <p className="text-3xl font-bold mt-1">{stats?.online_shoppers || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <Monitor className="w-6 h-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm font-medium">In-Store Shoppers</p>
                <p className="text-3xl font-bold mt-1">{stats?.instore_shoppers || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <Store className="w-6 h-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Total Active</p>
                <p className="text-3xl font-bold mt-1">{stats?.total_active || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <Users className="w-6 h-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Cart Value</p>
                <p className="text-3xl font-bold mt-1">${(stats?.total_cart_value || 0).toFixed(2)}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <DollarSign className="w-6 h-6" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Live Activity */}
      <Card className="mb-8">
        <CardHeader className="border-b">
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-green-500" />
            Live Shopping Activity
            {autoRefresh && (
              <span className="flex items-center gap-1 text-sm font-normal text-green-600">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
                Live
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {sessions.length === 0 ? (
            <div className="p-8 text-center">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No active shoppers at the moment</p>
              <p className="text-sm text-gray-400 mt-1">Activity will appear here when customers start shopping</p>
            </div>
          ) : (
            <div className="divide-y">
              {sessions.map((session) => (
                <div key={session.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      {/* Avatar */}
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        session.type === 'in-store' 
                          ? 'bg-orange-100 text-orange-600' 
                          : 'bg-blue-100 text-blue-600'
                      }`}>
                        {session.type === 'in-store' ? (
                          <Store className="w-6 h-6" />
                        ) : (
                          <Monitor className="w-6 h-6" />
                        )}
                      </div>
                      
                      {/* User Info */}
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-gray-800">{session.user_name}</h4>
                          <Badge className={
                            session.type === 'in-store' 
                              ? 'bg-orange-100 text-orange-700 border-orange-200' 
                              : 'bg-blue-100 text-blue-700 border-blue-200'
                          }>
                            {session.type === 'in-store' ? 'In-Store' : 'Online'}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-500">{session.user_email}</p>
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          <span className="flex items-center gap-1 text-gray-500">
                            <Clock className="w-4 h-4" />
                            {formatTime(session.last_activity)}
                          </span>
                          <span className="flex items-center gap-1 text-gray-500">
                            <Package className="w-4 h-4" />
                            {session.cart_items?.length || 0} items
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Cart Total */}
                    <div className="text-right">
                      <p className="text-2xl font-bold text-gray-800">
                        ${(session.cart_total || 0).toFixed(2)}
                      </p>
                      <p className="text-sm text-gray-500">Cart Value</p>
                    </div>
                  </div>

                  {/* Cart Items Preview */}
                  {session.cart_items && session.cart_items.length > 0 && (
                    <div className="mt-4 pl-16">
                      <div className="flex flex-wrap gap-2">
                        {session.cart_items.slice(0, 5).map((item, idx) => (
                          <div 
                            key={idx}
                            className="px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-700"
                          >
                            {item.name} × {item.quantity}
                          </div>
                        ))}
                        {session.cart_items.length > 5 && (
                          <div className="px-3 py-1 bg-gray-200 rounded-full text-sm text-gray-600">
                            +{session.cart_items.length - 5} more
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-gradient-to-r from-gray-50 to-gray-100 border-gray-200">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-800">About General Overseer</h4>
              <p className="text-sm text-gray-600 mt-1">
                This dashboard provides real-time visibility into customer shopping activity across all channels. 
                Sessions are automatically marked as abandoned after 30 minutes of inactivity.
              </p>
              <div className="flex flex-wrap gap-4 mt-3 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span className="text-gray-600">Online Shopping</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                  <span className="text-gray-600">In-Store Shopping</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GeneralOverseer;
